import { useCallback, useEffect, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { FieldErrors, UseFormRegister } from 'react-hook-form'
import classNames from 'classnames'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/router'
import { AccountDrawerMarketingImagesLocation } from 'shared-types'
import { Button } from '~/components/Button'
import { Form } from '~/components/Form'
import GoogleReCaptchaNotice from '~/components/GoogleReCaptchaNotice'
import { Icon } from '~/components/Icon'
import { Image } from '~/components/Image'
import Input from '~/components/Input'
import { Link } from '~/components/Link'
import { LoadingSpinner } from '~/components/LoadingSpinner'
import { PasswordInput } from '~/components/PasswordInput'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { getImageSrc } from '~/helpers'
import { useCustomer, useSession } from '~/hooks'
import { GTM, GTMEvent } from '~/lib'
import { LoginWithEmailAndPasswordSchema } from '~/validation-schemas'

import styles from './AuthenticationDrawer.module.scss'
import {
  AuthenticationDrawerContentProps,
  AuthenticationDrawersTypes,
} from './AuthenticationDrawer.types'

const GHOST_BUTTON_STYLE = {
  '--btn-size-md-padding': 0,
  '--btn-size-md-height': 'auto',
  '--btn-font-weight': 400,
  '--btn-ghost-text-decoration': 'underline',
  '--btn-ghost-border-width': 0,
} as React.CSSProperties

type SignInFormRegister = UseFormRegister<{
  email: string
  password: string
}>

type SignInFormErrors = FieldErrors<{
  email: string
  password: string
}>

const SignInAuthenticationDrawer = ({
  onClose,
  accountDrawerConfiguration,
}: AuthenticationDrawerContentProps) => {
  const router = useRouter()
  const { isFetchingCustomer, customer } = useCustomer()
  const { session } = useSession()
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()

  useEffect(() => {
    if (customer?.isSignedIn) {
      onClose(true)
    }
  }, [customer, onClose])

  const performRecaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      throw new Error('reCAPTCHA initialization error')
    }

    return executeRecaptcha('submit')
  }, [executeRecaptcha])

  const handleSignIn = async (data: { email: string; password: string }) => {
    if (loading) {
      return
    }

    setLoading(true)

    try {
      const recaptchaToken = await performRecaptcha()
      const response = await signIn('credentials', {
        username: encodeURIComponent(data.email),
        password: data.password,
        recaptchaToken,
        session: JSON.stringify(session),
        callbackUrl: '/account',
        redirect: false,
      })
      if (response.ok) {
        GTM.dispatch(GTMEvent.LOGIN)
      } else {
        setError(response.error || 'The email or password is incorrect')
      }
    } catch (err) {
      setError('The email or password is incorrect')
    } finally {
      setLoading(false)
    }
  }

  const isSignInLoading = loading || isFetchingCustomer

  const openRegistrationDrawer = useCallback(() => {
    router.replace(`#${AuthenticationDrawersTypes.Register}`, undefined, {
      shallow: true,
    })
  }, [router])

  const openPasswordResetDrawer = useCallback(() => {
    router.replace(`#${AuthenticationDrawersTypes.ForgotPassword}`, undefined, {
      shallow: true,
    })
  }, [router])

  const renderMembershipPerks = useCallback(() => {
    const membershipPerks = accountDrawerConfiguration?.membershipPerks || []

    if (!membershipPerks.length) {
      return null
    }

    return (
      <div className='text-[#0C254C] mb-5'>
        <Typography
          className='mb-4 font-semibold'
          tag={TypographyTag.div}
          variant={TypographyVariant.BodyRegularBold}>
          {accountDrawerConfiguration?.membershipPerksTitle}
        </Typography>
        <div>
          {membershipPerks.map((perk, index) => {
            const iconUrl = perk.iconUrl ? `url(${perk.iconUrl})` : undefined

            return (
              <div
                key={index}
                className={classNames(
                  'rounded-md border border-greyscale-300',
                  'flex items-center gap-2.5',
                  'mb-4 last:mb-0 py-3 px-4'
                )}>
                <div
                  className='rounded-full min-h-[36px] min-w-[36px] h-[36px] w-[36px]'
                  style={{
                    backgroundColor: perk.backgroundColor,
                    backgroundImage: iconUrl,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                <Typography
                  className='text-black'
                  tag={TypographyTag.div}
                  variant={TypographyVariant.BodyRegularBold}>
                  {perk.label}
                </Typography>
              </div>
            )
          })}
        </div>
      </div>
    )
  }, [accountDrawerConfiguration])

  const renderMarketingImages = useCallback(
    (location: AccountDrawerMarketingImagesLocation) => {
      const marketingImages = accountDrawerConfiguration?.marketingImages || []

      if (
        accountDrawerConfiguration?.marketingImagesLocation !== location ||
        !marketingImages.length
      ) {
        return null
      }

      return (
        <div className='mb-4'>
          {marketingImages.map((image, index) => {
            return (
              <div key={index} className='mb-4 last:mb-0 relative'>
                {image.link && (
                  <Link
                    target='_blank'
                    className='absolute inset-0 z-20'
                    href={image.link}
                  />
                )}
                <Image
                  className='rounded-md w-full relative z-10'
                  src={getImageSrc(image.imageUrl, '600px')}
                />
              </div>
            )
          })}
        </div>
      )
    },
    [accountDrawerConfiguration]
  )

  const renderSignInForm = useCallback(
    (register: SignInFormRegister, errors: SignInFormErrors, error: string) => {
      return (
        <div className='mt-6 mb-4'>
          <Typography
            className='mb-4'
            variant={TypographyVariant.Heading6}
            tag={TypographyTag.p}>
            Sign in
          </Typography>
          <div className={classNames(styles.formContainer, 'mb-8')}>
            <Input
              label='Email Address'
              required
              type='text'
              placeholder='Enter your email address'
              name='email'
              register={register}
              error={errors.email?.message.toString()}
            />
            <PasswordInput
              required
              name='password'
              label='Password'
              placeholder='Enter your password'
              type='password'
              register={register}
              error={errors.password?.message.toString()}
            />
          </div>
          {error?.length > 0 && (
            <div className='flex gap-4 mt-4 items-center mb-8'>
              <Icon
                iconName='alert-triangle-filled'
                size={16}
                color='var(--color-state-alert)'
                className='min-w-[16px] min-h-[16px]'
              />
              <Typography
                tag={TypographyTag.p}
                variant={TypographyVariant.BodySmallBold}
                className='text-alert'>
                {error}
              </Typography>
            </div>
          )}
          <div className='mb-4'>
            <Button
              data-id='logInButton'
              htmlType='submit'
              status='primary'
              disabled={isSignInLoading}
              fluid
              size='lg'>
              Sign In
            </Button>
          </div>
          <div className='text-right'>
            <Button
              style={GHOST_BUTTON_STYLE}
              variant='ghost'
              onClick={openPasswordResetDrawer}>
              <Typography
                tag={TypographyTag.span}
                variant={TypographyVariant.BodyRegularMedium}>
                Forgot Password?
              </Typography>
            </Button>
          </div>
        </div>
      )
    },
    [isSignInLoading, openPasswordResetDrawer]
  )

  const renderRegistrationCta = useCallback(() => {
    return (
      <div className='text-[#0C254C] mb-4'>
        <Typography
          className='mr-1'
          tag={TypographyTag.span}
          variant={TypographyVariant.BodyRegular}>
          New to Jaycar?
        </Typography>
        <div className='inline-block'>
          <Button
            style={GHOST_BUTTON_STYLE}
            variant='ghost'
            className='normal-case'
            onClick={openRegistrationDrawer}>
            <Typography
              tag={TypographyTag.span}
              variant={TypographyVariant.BodyRegularMedium}>
              {accountDrawerConfiguration?.signUpButtonLabel}
            </Typography>
          </Button>
          <Typography
            className={classNames(
              'inline-block rounded-md bg-[#7A9E19]',
              'text-white font-semibold',
              'px-2 py-0.5 mx-2'
            )}
            tag={TypographyTag.span}
            variant={TypographyVariant.BodySmall}>
            Free
          </Typography>
        </div>
      </div>
    )
  }, [accountDrawerConfiguration?.signUpButtonLabel, openRegistrationDrawer])

  return (
    <Form
      data-id='logInForm'
      className={styles.formWrapper}
      validationSchema={LoginWithEmailAndPasswordSchema}
      useFormProps={{ mode: 'onChange' }}
      onSubmit={(data) => {
        handleSignIn(data)
      }}>
      {({ register, formState: { errors } }) => {
        return (
          <div className='relative flex-1 py-8 px-6 md:px-8'>
            {isSignInLoading && (
              <>
                <div className='absolute inset-0 flex justify-center bg-spinner-overlay z-10' />
                <div className='absolute z-20 inset-x-0 flex justify-center pt-32'>
                  <LoadingSpinner />
                </div>
              </>
            )}
            <div>
              {renderMarketingImages(AccountDrawerMarketingImagesLocation.Top)}
              {renderSignInForm(register, errors, error)}
              {renderMarketingImages(
                AccountDrawerMarketingImagesLocation.Middle
              )}
              {renderRegistrationCta()}
              {renderMembershipPerks()}
              {renderMarketingImages(
                AccountDrawerMarketingImagesLocation.Bottom
              )}
              <GoogleReCaptchaNotice className='mt-4' />
            </div>
          </div>
        )
      }}
    </Form>
  )
}

export default SignInAuthenticationDrawer
